import { TableColumnsType, Button, Typography, Form, Row, Col, Input, Select, Tooltip, App, Empty } from 'antd';
import { Table } from '@/components/tables/table';
import { css, Global } from '@emotion/react';
import { DeleteOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getFoodRecommendBasedOnHealthStatus, watchFoodRecommendBasedOnHealthStatusList, watchFoodRecommendBasedOnHealthStatusPagination, watchHealthStatusList, deleteFoodRecommendation, watchHealthStatusLoading, getListHealthStatus, resetMessages, watchFoodRecommendBasedOnHealthStatusMessage } from '@/redux/slices/health-status.slice';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { setMessages } from '@/redux/slices/app.slice';

type RecommendFoodManagementTableProps = {
    setActiveTabProps: React.Dispatch<React.SetStateAction<string>>;
};

type PageChangeParams = {
    page?: number;
    pageSize?: number;
    filter?: FilterRecommendationFood;
};

export default function RecommendFoodManagementTable({
    setActiveTabProps
}: RecommendFoodManagementTableProps) {
    const [form] = Form.useForm();
    const { modal } = App.useApp();

    const dispatch = useAppDispatch();
    const foodRecommendBasedOnHealthStatusList = useAppSelector(watchFoodRecommendBasedOnHealthStatusList);
    const healthStatusList = useAppSelector(watchHealthStatusList);
    const pagination = useAppSelector(watchFoodRecommendBasedOnHealthStatusPagination);
    const loading = useAppSelector(watchHealthStatusLoading);
    const [filter, setFilter] = useState<FilterRecommendationFood>({});
    const messageInfo = useAppSelector(watchFoodRecommendBasedOnHealthStatusMessage);

    useEffect(() => {
        dispatch(getListHealthStatus({
            offset: 0,
            limit: PAGINATION_PARAMS.unLimit,
        }));
    }, [dispatch]);

    const healthStatusOptions = useMemo(() => [
        ...(healthStatusList || []).map((item: any) => ({
            value: item.healthStatusId,
            label: item.displayName,
        })),
    ], [healthStatusList]);

    const foodTypeOptions = useMemo(() => [
        { value: 'NENAN', label: 'Nên ăn' },
        { value: 'HANCHEAN', label: 'Hạn chế ăn' },
    ], []);

    const tableData = foodRecommendBasedOnHealthStatusList?.map((item: any, _index: number) => ({
        key: item.id,
        id: item.id,
        foodName: item.foodName,
        foodType: item.typeAdvice === 'NENAN' ? 'Nên ăn' : 'Hạn chế ăn',
        healthStatusId: item.healthStatusId,
    })) || [];

    const handlePageChange = useCallback(
        ({
            page = PAGINATION_PARAMS.offset,
            pageSize = PAGINATION_PARAMS.limit,
            filter
        }: PageChangeParams) => {
            const offset = (page - 1) * pageSize;
            dispatch(
                getFoodRecommendBasedOnHealthStatus({
                    offset,
                    limit: pageSize,
                    search: filter?.foodName,
                    healthStatusId: filter?.healthStatus,
                    typeAdvice: filter?.foodType
                })
            );
        },
        [dispatch]
    );

    useEffect(() => {
        if (messageInfo) {
          dispatch(
            setMessages([
              {
                type: messageInfo.type,
                message: messageInfo.message,
                description: messageInfo.description
              }
            ])
          );
          dispatch(resetMessages());
        }
      }, [dispatch, messageInfo]);

    const columns: TableColumnsType<any> = [
        {
            title: 'STT',
            render: (_, __, index) => pagination.curPage + index + 1,
            width: 1,
        },
        {
            title: 'Thực phẩm',
            dataIndex: 'foodName',
            key: 'foodName',
            width: 200,
        },
        {
            title: 'Phân loại',
            dataIndex: 'foodType',
            key: 'foodType',
            width: 200
        },
        {
            title: 'Thao tác',
            key: 'action',
            fixed: 'right',
            width: 104,
            align: 'center',
            render: (record) => (
                <Tooltip title='Xoá'>
                    <Button
                      type='primary'
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => confirmDelete(record.id)}
                    />
                </Tooltip>
          ),
        },
    ];

    const confirmDelete = (id: number) => {
        modal.confirm({
            title: "Bạn có chắc chắn xoá dữ liệu này?",
            content: (
                <em>
                    Dữ liệu không thể khôi phục
                </em>
            ),
            okText: 'Xóa',
            width: 450,
            cancelText: 'Hủy',
            onOk: () => {
                dispatch(deleteFoodRecommendation(id));
                dispatch(getFoodRecommendBasedOnHealthStatus({
                    offset: pagination.curPage,
                    limit: pagination.limitPage,
                    search: filter?.foodName,
                    healthStatusId: filter?.healthStatus,
                    typeAdvice: filter?.foodType
                }));
            },
        });
    };

    const handleFormChange = (_: any, allValues: any) => {
        const { healthStatus, foodName, foodType } = allValues;
        setFilter({ healthStatus, foodName, foodType });
        handlePageChange({
            page: 1,
            pageSize: pagination.limitPage,
            filter: {
                healthStatus,
                foodName,
                foodType
            }
        });
    };

    useEffect(() => {
        handlePageChange({});
    }, [handlePageChange]);

    const currentPage = Math.floor(pagination.curPage / pagination.limitPage) + 1;

    return (
        <>
            <Global
                styles={css`
                    .ant-modal-confirm-btns {
                        display: flex !important;
                        justify-content: center !important;
                        padding-top: 2rem;
                    }
                `}
            />
            <div css={boxStyles}>
                <div css={headerStyles}>
                    <Typography.Title level={5}>Danh sách thực phẩm nên ăn/ hạn chế ăn theo tình trạng sức khỏe</Typography.Title>
                    <Button
                        onClick={() => setActiveTabProps('IMPORT_RCM_FOOD_BY_LIST')}
                        css={listInputButtonStyles}
                    >
                        Nhập theo danh sách khuyến nghị về thực phẩm
                    </Button>
                </div>
    
                <Form
                    form={form}
                    css={formStyles}
                    onValuesChange={handleFormChange}
                >
                    <Row gutter={[24, 16]} align="stretch">
                        <Col span={24} css={filterItem}>
                            <Form.Item
                                label="Tình trạng sức khỏe"
                                name="healthStatus"
                                css={[customLabel, customColor]}
                                labelCol={{ span: 4 }}
                                colon={false}
                            >
                                <Select options={healthStatusOptions} placeholder="Tình trạng sức khỏe" allowClear />
                            </Form.Item>
                        </Col>
                    </Row>
    
                    <Row gutter={[24, 16]} align="stretch" css={{ marginTop: '1rem' }}>
                        <Col span={12} css={filterItem}>
                            <Form.Item
                                label="Tên thực phẩm"
                                name="foodName"
                                css={customLabel}
                                labelCol={{ span: 8 }}
                                colon={false}
                            >
                                <Input placeholder="Nhập để tìm kiếm theo tên thực phẩm" allowClear style={{ marginLeft: '0.37rem'}} />
                            </Form.Item>
                        </Col>
                        <Col span={12} css={filterItem}>
                            <Form.Item
                                label="Phân loại"
                                name="foodType"
                                css={customLabel}
                                labelCol={{ span: 8 }}
                                colon={false}
                            >
                                <Select options={foodTypeOptions} placeholder="Phân loại" allowClear />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
                <Table<any>
                    columns={columns}
                    dataSource={tableData}
                    pagination={{
                        current: currentPage,
                        pageSize: pagination.limitPage,
                        total: pagination.totalRows,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        onChange: (page, pageSize) => {
                            handlePageChange({ page, pageSize, filter });
                        }
                    }}
                    loading={loading}
                    locale={{ emptyText: <Empty description="Không có kết quả!" /> }}
                />
            </div>
        </>
    );
}

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none;
  }
`;

const listInputButtonStyles = css`
  background-color: var(--success-color);
  color: var(--white-color);
  border-color: var(--success-color);

  &:hover {
    background-color: var(--success-color) !important;
    border-color: var(--success-color) !important;
  }
`;

const headerStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
`;

const formStyles = css`
  margin-bottom: 1rem;
`;

const filterItem = css`
  display: flex;
  justify-content: start !important;
  gap: 0.5rem;

  label {
    height: 100% !important;
  }

  .ant-form-item-label {
    width: fit-content;
    height: 100% !important;
    text-align: start !important;
    white-space: normal;
    font-size: 14px;
    font-weight: 500;
    word-wrap: break-word;
    word-break: break-word;
  }

  input,
  .ant-select {
    flex: 1;
    min-width: 6rem;
  }
`;

const customLabel = css`
  .ant-form-item-label > label {
    font-weight: normal !important;
  }

  width: 100%;
  margin-bottom: 0;
`;

const customColor = css`
  .ant-form-item-label > label {
    color: #1890FF;
    font-weight: bold !important;
  }
`
